import React from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import MenuItem from '@material-ui/core/MenuItem';

import { createHrisIntegrationResponse } from './actions/create-hris-integration-response';
import { updateHrisIntegrationResponse } from './actions/update-hris-integration-response';
import { allHrisIntegrationTypesSelector } from '../merge-integration/hris-integration-type-selectors';

import { UiButton } from '../ui/ui-button';

import { sortAlphabetically, SORT_ORDER } from '../util/sort-functions';
import { FormSelect } from '../widgets/react-hook-form-components/form-select';

type InputProps = {
  selectProofed: boolean;
  hrisIntegrationResponse?: HrisIntegrationResponse;
  org: Org;
  handleNext: () => void;
  handlePrevious?: () => void;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    createHrisIntegrationResponse: (response: HrisIntegrationResponseCreateRequest) =>
      dispatch(createHrisIntegrationResponse(response)),
    updateHrisIntegrationResponse: (update: HrisIntegrationResponseUpdateRequest) =>
      dispatch(updateHrisIntegrationResponse(update)),
  },
});

export const HrisIntegrationResponseIntegrationSelect = ({
  selectProofed,
  hrisIntegrationResponse,
  org,
  handleNext,
  handlePrevious,
}: InputProps) => {
  const integrationTypes = useSelector((state: any) => allHrisIntegrationTypesSelector(state));

  const { actions } = mapDispatchToProps(useDispatch());

  const integrationNames = integrationTypes
    .filter(({ proofedAt }) => Boolean(proofedAt) === selectProofed)
    .map(({ integrationName }) => integrationName)
    .sort(sortAlphabetically((name) => name, SORT_ORDER.ASC));
  const integrationOptions = [...integrationNames, 'Other'];

  const getDefaultIntegrationName = () => {
    // if the response integrationName is an option on the integration select,
    // default the select to that. else, default it to 'Other'
    if (hrisIntegrationResponse?.HrisIntegrationType?.integrationName) {
      return integrationNames.includes(hrisIntegrationResponse.HrisIntegrationType.integrationName)
        ? hrisIntegrationResponse.HrisIntegrationType.integrationName
        : 'Other';
    }

    if (
      // if user is selecting from proofed integrations and there is a response
      // but no integrationName
      (selectProofed && hrisIntegrationResponse?.HrisIntegrationType?.integrationName === null) ||
      // or if the response includes a manually input unsupportedIntegrationName
      hrisIntegrationResponse?.unsupportedIntegrationName
    ) {
      // default the select to 'Other'
      return 'Other';
    }

    // if no response, default value should be empty
    return '';
  };

  const defaultValues = {
    integrationName: getDefaultIntegrationName(),
  };
  const {
    control,
    errors,
    formState: { isDirty, isValid },
    handleSubmit,
    watch,
  } = useForm({ mode: 'all', defaultValues });

  const integrationNameWatch = watch('integrationName');

  const onSubmit: SubmitHandler<typeof defaultValues> = ({
    integrationName: integrationNameValue,
  }) => {
    if (isDirty) {
      const createOrUpdateAttributes =
        integrationNameValue !== 'Other'
          ? {
              HrisIntegrationType: { integrationName: integrationNameValue },
              unsupportedIntegrationName: null,
            }
          : {
              HrisIntegrationType: null,
            };
      if (hrisIntegrationResponse) {
        actions.updateHrisIntegrationResponse({
          ...hrisIntegrationResponse,
          ...createOrUpdateAttributes,
        });
      } else {
        actions.createHrisIntegrationResponse({
          orgId: org.org_id,
          ...createOrUpdateAttributes,
        });
      }
    }

    handleNext();
  };

  const getSmartlookClickInfo = (action: string) => ({
    eventName: 'HRIS response dialog click',
    props: {
      component: 'integration select',
      selectProofed,
      integrationName: integrationNameWatch,
      action,
    },
  });

  return (
    <>
      <div>
        {selectProofed
          ? 'Team Engine syncs with a number of HR/payroll systems to keep your employee directory up to date.'
          : 'We are able to support several dozen other HRIS/Payroll systems.'}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="u-margin-top typo-subtitle">Select your HRIS/payroll system</div>
        {control && (
          <FormSelect
            control={control}
            name="integrationName"
            error={errors.integrationName}
            required
          >
            {integrationOptions.map((integration) => (
              <MenuItem key={integration} value={integration}>
                {integration}
              </MenuItem>
            ))}
          </FormSelect>
        )}
      </form>
      <div
        className={`u-flex-center u-margin-top ${
          handlePrevious ? 'u-justifyContent-spaceBetween' : 'u-justifyContent-flexEnd'
        }`}
      >
        {handlePrevious && (
          <UiButton
            variant="tertiary"
            size="sm"
            onClick={handlePrevious}
            smartlookEvent={getSmartlookClickInfo('previous')}
          >
            Previous
          </UiButton>
        )}
        <UiButton
          type="submit"
          variant="primary"
          size="sm"
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
          smartlookEvent={getSmartlookClickInfo('next')}
        >
          Next
        </UiButton>
      </div>
    </>
  );
};
